import { useMemo } from 'react'

import { Tag, Tooltip } from 'antd'
import { useSelector } from 'react-redux'
import { faBan, faCheck, faCheckDouble, faEye, faUndo } from '@fortawesome/pro-solid-svg-icons'

import DataTable from '../../../Components/antd/Tables/DataTable/DataTable'
import TooltipIconButton from '../../../Components/antd/Buttons/TooltipIconButton'
import { Action } from '../../../Components/antd/Tables/DataTable/utils/actions'
import { buildFilters, Filter } from '../../../Components/antd/Tables/DataTable/utils/tableFilter'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { OrderBy } from '../../../utils/constants'
import LOCAL_STORAGE_KEYS from '../../../utils/localStorage'
import AphmAnomalyFilters, { getActiveFiltersCount } from './AphmAnomalyFilters'
import {
  formatStampDate,
  formatStampDateTime,
  getSeverityTag,
  getStatusTag,
  getTypeLabel,
  isVolume,
  Statuses
} from '../../../utils/entities/aphmAnomaly'

const TAG_STYLE = { borderRadius: '8px', fontWeight: 500 }

const AphmAnomaliesTable = ({
  data,
  hasUnseen,
  loading,
  parameters,
  onFiltersChange,
  onMarkSeen,
  onParametersChange,
  onResetFilters,
  onHandle,
  onShowDetail
}) => {
  const t = useSelector(selectTranslate)

  const columns = useMemo(() => [
    {
      title: t('aphm_anomaly.severity_label'),
      key: anomaly => {
        const tag = getSeverityTag(anomaly.severity)

        return <Tag color={tag.color} style={TAG_STYLE}>{t(tag.label)}</Tag>
      },
      orderBy: OrderBy.SEVERITY
    },
    {
      title: t('common.type'),
      key: anomaly => <Tag style={TAG_STYLE}>{t(getTypeLabel(anomaly.type))}</Tag>,
      orderBy: OrderBy.TYPE
    },
    {
      title: t('common.status'),
      key: anomaly => {
        const tag = getStatusTag(anomaly.status)

        return <Tag color={tag.color} style={TAG_STYLE}>{t(tag.label)}</Tag>
      },
      orderBy: OrderBy.STATE
    },
    {
      title: t('aphm_anomaly.aphm_id'),
      key: anomaly => isVolume(anomaly) ? '—' : (anomaly.aphmId ?? '—')
    },
    {
      title: t('aphm_anomaly.shift_count'),
      key: anomaly => anomaly.shiftCount ?? 0,
      orderBy: OrderBy.SHIFT_COUNT
    },
    {
      title: t('aphm_anomaly.occurrences'),
      key: anomaly => anomaly.occurrences > 1
        ? (
          <Tooltip title={t('aphm_anomaly.last_seen_at') + ' : ' + (formatStampDateTime(anomaly.lastSeenAt) ?? '')}>
            <span>{anomaly.occurrences}</span>
          </Tooltip>
        )
        : anomaly.occurrences,
      orderBy: OrderBy.OCCURRENCES
    },
    {
      title: t('data_table.column_title.creation_date'),
      key: anomaly => formatStampDate(anomaly.createdAt) ?? '',
      orderBy: OrderBy.CREATED_AT
    }
  ], [t])

  const filters = useMemo(() => buildFilters([
    new Filter({
      key: 'filters',
      label: t('Filters'),
      component: AphmAnomalyFilters,
      props: { filters: parameters.filters, onFiltersChange }
    })
  ]), [parameters.filters, onFiltersChange, t])

  const actions = useMemo(() => [
    new Action({
      title: 'aphm_anomaly.actions.detail',
      icon: faEye,
      onClick: anomaly => onShowDetail(anomaly)
    }),
    new Action({
      toRender: anomaly => anomaly.status === Statuses.OPEN,
      title: 'aphm_anomaly.actions.handle',
      icon: faCheck,
      onClick: anomaly => onHandle(anomaly, Statuses.HANDLED)
    }),
    new Action({
      toRender: anomaly => anomaly.status === Statuses.OPEN,
      title: 'aphm_anomaly.actions.ignore',
      icon: faBan,
      buttonType: 'danger',
      onClick: anomaly => onHandle(anomaly, Statuses.IGNORED)
    }),
    new Action({
      toRender: anomaly => anomaly.status !== Statuses.OPEN,
      title: 'aphm_anomaly.actions.reopen',
      icon: faUndo,
      onClick: anomaly => onHandle(anomaly, Statuses.OPEN)
    })
  ], [onHandle, onShowDetail])

  const markSeenButton = useMemo(() => (
    <TooltipIconButton
      disabled={!hasUnseen}
      icon={faCheckDouble}
      text={t('aphm_anomaly.actions.mark_seen')}
      title={t('aphm_anomaly.actions.mark_seen_title')}
      onClick={onMarkSeen}
    />
  ), [hasUnseen, onMarkSeen, t])

  return (
    <DataTable
      actions={actions}
      columns={columns}
      data={data}
      filters={filters}
      getActiveFiltersCount={getActiveFiltersCount}
      filtersMenu={markSeenButton}
      loading={loading}
      metadata={parameters.metadata}
      parameters={parameters}
      storageKey={LOCAL_STORAGE_KEYS.APHM_ANOMALIES_VIEW.ROOT}
      onParametersChange={onParametersChange}
      onResetFilters={onResetFilters}
    />
  )
}

export default AphmAnomaliesTable
